import React, { useState, useEffect, useCallback, useContext } from 'react';
import Codigo from './Codigo';
import Load from './Load';
import { Get_type_code_name } from '../Comun';
import ModalEliminacion from '../modal/ModalEliminacion';
import FilterModal from '../modal/FilterCodigoModal';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { useParams, useNavigate } from 'react-router-dom';

interface Code
{
    id: number;
    code: string;
    response: string;
    type: number;
    id_tag: number;
    number_of_uses: number;
    number_of_uses_used: number;
    id_provider: number;
}

interface Tag
{
    id: number;
    tag: string;
    valid_time: string;
    type_tag: number;
}

interface Provider
{
    id: number;
    name: string;
}

const Codigos: React.FC = () =>
{
    const { eventId, clientId } = useParams();
    const navigate = useNavigate();
    const eventIdNum = Number(eventId);

    const { token } = useContext(AuthContext);

    const [codes, setCodes] = useState<Code[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [uploading, setUploading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCode, setSelectedCode] = useState<Code | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
    const [isLoadOpen, setIsLoadOpen] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [filters, setFilters] = useState({
        is_root_response: true as boolean,
        response: null as string | null,
        code: null as string | null,
        type: null as number | null,
        id_tag: null as number | null,
        id_provider: null as number | null,
    });
    const [title, setEventName] = useState<string>('');

    //-----------------------------------------------------------

    const fetchCodes = useCallback(async (page: number = 1, uploading = false) =>
    {
        setLoading(!uploading);
        setUploading(uploading);
        try
        {
            const queryParams = new URLSearchParams({
                id_event: eventIdNum.toString(),
                id_client: Number(clientId).toString(),
                page: page.toString(),
                ...(filters.code && { code: filters.code }),
                ...(filters.type !== null && { type: filters.type.toString() }),
                ...(filters.id_tag !== null && { id_tag: filters.id_tag.toString() }),
                ...(filters.id_provider !== null && { id_provider: filters.id_provider.toString() }),
                ...(filters.response && { [filters.is_root_response ? 'root_response' : 'response']: filters.response })
            });

            const response = await apiFetch(`web/search_event_codes_filter?${queryParams.toString()}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                const data = await response.json();
                setCodes(data.results);
                setTotalResults(data.total_results);
                setTotalPages(data.total_pages);
                setCurrentPage(page);
            }
            else
            {
                navigate('/main/not-found');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
        finally
        {
            setLoading(false);
        }
    }, [eventId, filters, token]);

    //-----------------------------------------------------------

    const fetchTags = useCallback(async () =>
    {
        try
        {
            const response = await apiFetch(`web/get_event_tags?id_event=${eventId}&id_client=${clientId}`, {
                method: 'GET',
            });

            if (response.ok)
            {
                const data = await response.json();
                setTags(data.tags);
                setEventName(data.eventName);
            }
            else
            {
                throw new Error('Error al cargar tags.');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
    }, [eventId, token]);

    //-----------------------------------------------------------

    const fetchProviders = useCallback(async () =>
    {
        try
        {
            const response = await apiFetch(`web/get_event_providers_basic_info?id_event=${eventId}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                const data = await response.json();
                setProviders(data);
            }
            else
            {
                throw new Error('Error al cargar providers.');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
    }, [eventId, token]);

    //-----------------------------------------------------------
    
    const fetchExportCodereadr = useCallback(async () =>
    {
        setUploading(true);
        try
        {
            const queryParams = new URLSearchParams({
                id_event: eventIdNum.toString(),
            });

            const response = await apiFetch(`web/export_codes_codereadr?${queryParams.toString()}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                fetchCodes(1, true);
            }
            else
            {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al sincronizar con CodeREADr.');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
        finally
        {
            setUploading(false);
        }
    }, [eventId, filters, token]);
    
    //-----------------------------------------------------------

    useEffect(() =>
    {
        const loadInitialData = async () =>
        {
            try
            {
                await fetchTags();
                await fetchProviders();
            }
            catch (error)
            {
                setError('Error al cargar datos iniciales.');
            }
        };

        loadInitialData();
    }, [fetchTags, fetchProviders]);

    //-----------------------------------------------------------

    useEffect(() =>
    {
        fetchCodes(currentPage);
    }, [fetchCodes, currentPage, filters]);

    //-----------------------------------------------------------

    const handleCSVClick = () => { setIsLoadOpen(true); };

    const handleCreateClick = () =>
    {
        setSelectedCode({
            id: -1,
            code: '',
            response: '',
            type: -1,
            id_tag: -1,
            number_of_uses: 1,
            number_of_uses_used: 0,
            id_provider: -1
        });
    };

    const handleEditClick = (code: Code) => { setSelectedCode(code); };

    const handleFormClose = () =>
    {
        setSelectedCode(null);
        fetchCodes(currentPage);
    };

    const handleDeleteAllClick = () => { setIsModalOpen(true); };

    const handleConfirmDelete = async () =>
    {
        setIsModalOpen(false);
        try
        {
            const formData = new URLSearchParams();
            const response = await apiFetch(`web/delete_all_event_codes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formData.toString()
            });

            if (response.ok) { setCodes([]); }
            else { throw new Error('Error al borrar todos los códigos.'); }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
    };

    const handleCancelDelete = () => { setIsModalOpen(false); };

    const handleFilterOpen = () => { setIsFilterModalOpen(true); };

    const handleApplyFilters = (filters: {
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        id_provider: number | null;
    }) =>
    {
        setFilters(filters);
        setCurrentPage(1);
    };

    const handleClearFilters = () =>
    {
        setFilters({
            is_root_response: true,
            response: null,
            code: null,
            type: null,
            id_tag: null,
            id_provider: null
        });
        setCurrentPage(1);
    };

    const handleExportCodesCodereadrClick = async () =>
    {
        try
        {
            await fetchExportCodereadr();
        }
        catch (error)
        {
            setError('Error al sincronizar.');
        }
    };

    const Get_tag_name = (id: number): string =>
    {
        const tag = tags.find(t => t.id === id);
        return tag ? tag.tag : '<desconocido>';
    };

    const Get_provider_name = (id: number): string =>
    {
        const prov = providers.find(p => p.id === id);
        return prov ? prov.name : '<sin proveedor>';
    };

    const handlePageChange = (newPage: number) =>
    {
        if (newPage > 0 && newPage <= totalPages) { setCurrentPage(newPage); }
    };

    //-----------------------------------------------------------

    /* ── Helpers para la pill de tipo ── */
    const getTypePillClass = (type: number): string =>
    {
        const name = Get_type_code_name(type).toLowerCase();
        if (name.includes('online')) return 'cod-pill cod-pill--online';
        if (name.includes('offline')) return 'cod-pill cod-pill--offline';
        return 'cod-pill cod-pill--offline';
    };

    //-----------------------------------------------------------

    return (
        <div className="eventos-container">
            <div className="miguitas">Códigos de {title}</div>

            {selectedCode === null ? (
                <div className="content-wrapper">

                    {/* ── Barra de acciones ── */}
                    <div className="cod-toolbar">
                        <button
                            className="icon-btn"
                            onClick={() => navigate(`/main/recintos/${clientId}/eventos`)}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 19l-7-7 7-7"/></svg>
                            Volver a Eventos
                        </button>

                        <div className="cod-toolbar__right">
                            <button className="icon-btn" onClick={handleFilterOpen}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
                                Filtro
                            </button>
                            
                            <button className="icon-btn" onClick={handleExportCodesCodereadrClick}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path d="M3 5v14"/>
                                    <path d="M21 12H7"/>
                                    <path d="m15 18 6-6-6-6"/>
                                </svg>
                                Exportar a CodeREADr
                            </button>
                            <button className="icon-btn" onClick={handleCSVClick}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                                CSV
                            </button>
                            <button className="icon-btn icon-btn--create" onClick={handleCreateClick}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                                Crear código
                            </button>
                            <button className="icon-btn icon-btn--danger" onClick={handleDeleteAllClick}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                                Borrar BBDD
                            </button>
                        </div>
                    </div>

                    {/* ── Errores ── */}
                    {error && <p className="error-message">{error}</p>}

                    {/* ── Tabla ── */}
                    <div className="codes-container">
                        {loading && !error && <p className="loading-message">Obteniendo códigos...</p>}
                        {uploading && !error && <p className="loading-message">Exportando códigos...</p>}
                        {!loading && !error && codes.length === 0 && <p>No hay códigos para mostrar.</p>}

                        {!loading && !error && codes.length > 0 && (
                            <div className="table-scroll">
                                <table className="eventos-table cod-table">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Usos</th>
                                            <th>Usados</th>
                                            <th>Tipo</th>
                                            <th>Tag</th>
                                            <th>Respuesta</th>
                                            <th>Proveedor</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {codes.map(code => {
                                            const agotado = code.number_of_uses_used >= code.number_of_uses;
                                            return (
                                                <tr key={code.id}>
                                                    <td className="cod-code-cell">
                                                        {code.code}
                                                    </td>
                                                    <td className="cod-uses-cell">
                                                        {code.number_of_uses === 1 ? '1 uso' : `${code.number_of_uses} usos`}
                                                    </td>
                                                    <td className={agotado ? 'cod-used-cell cod-used-cell--full' : 'cod-used-cell'}>
                                                        {code.number_of_uses_used === 1 ? '1 usado' : `${code.number_of_uses_used} usados`}
                                                    </td>
                                                    <td>
                                                        <span className={getTypePillClass(code.type)}>
                                                            {Get_type_code_name(code.type)}
                                                        </span>
                                                    </td>
                                                    <td className="cod-muted-cell">{Get_tag_name(code.id_tag)}</td>
                                                    <td className="cod-muted-cell">{code.response ? code.response : '—'}</td>
                                                    <td className="cod-muted-cell">{Get_provider_name(code.id_provider)}</td>
                                                    <td className="evento-actions">
                                                        <button
                                                            className="icon-btn icon-btn--edit"
                                                            onClick={() => handleEditClick(code)}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                            Editar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── Paginación ── */}
                    {!loading && codes.length > 0 && (
                        <div className="pagination-controls">
                            <div className="pagination-buttons">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1}>
                                    <i className="fas fa-caret-left"></i>
                                </button>
                                <span>{currentPage.toLocaleString('es-ES')} / {totalPages.toLocaleString('es-ES')}</span>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= totalPages}>
                                    <i className="fas fa-caret-right"></i>
                                </button>
                            </div>
                            <div className="pagination-info">
                                <span>{totalResults.toLocaleString('es-ES')} resultados</span>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="form-overlay">
                    <Codigo
                        code={selectedCode}
                        id_event={eventIdNum}
                        tags={tags}
                        onSave={handleFormClose}
                        onCancel={handleFormClose}
                        onDelete={(codeId) => {
                            setCodes(codes.filter(code => code.id !== codeId));
                            handleFormClose();
                        }}
                    />
                </div>
            )}

            {isModalOpen && (
                <ModalEliminacion
                    isOpen={isModalOpen}
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}>
                    <p>¿Estás seguro de que quieres eliminar todos los códigos?</p>
                </ModalEliminacion>
            )}

            {isFilterModalOpen && (
                <FilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    tags={tags}
                    providers={providers}
                    currentFilters={filters}
                />
            )}

            {isLoadOpen && (
                <div className="form-overlay">
                    <Load
                        eventId={eventIdNum}
                        title={title}
                        onSave={() => setIsLoadOpen(false)}
                        onCancel={() => setIsLoadOpen(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default Codigos;
